import { Injectable, inject } from '@angular/core';
import {
  ConnectorExportRequirements,
  ConnectorExportResponse
} from '@softdocs/capture-ui-libraries';
import { AuthService } from '../auth/auth.service';
import { AutoclassifierSdkService } from '../autoclassifier-sdk/autoclassifier-sdk.service';
import { FieldWithBbox } from '../../models/transcriptExtractionData.model';
import { BatchSisResponse, SisErrorCodes } from '../../models/sis-response.model';
import { TranscriptToExport } from '../../models/etrive.model';
import { TranscriptType } from '../../constants';
import { mapCourses, mapSection } from '../../utils/transcript-payload.util';

/** Connector error codes mapped onto the codes the UI already reacts to. */
const CONNECTOR_ERROR_CODE_MAP: Record<string, string> = {
  DATA_VALIDATION_EXCEPTION: SisErrorCodes.DATA_VALIDATION,
  CONFIGURATION_EXCEPTION: SisErrorCodes.CONFIGURATION_ERROR,
  SFTP_PATH_NOT_FOUND_EXCEPTION: SisErrorCodes.CONFIGURATION_ERROR,
  SFTP_OPERATION_PERMISSION_DENIED_EXCEPTION: SisErrorCodes.CONFIGURATION_ERROR,
  SSH_AUTHENTICATION_EXCEPTION: SisErrorCodes.AUTHENTICATION_ERROR,
  SSH_CONNECTION_EXCEPTION: SisErrorCodes.SIS_UNAVAILABLE,
  INTERNAL_SERVER_ERROR: SisErrorCodes.UNKNOWN_ERROR,
  STUDENT_NOT_FOUND_EXCEPTION: SisErrorCodes.STUDENT_NOT_FOUND,
  INSTITUTION_NOT_FOUND_EXCEPTION: SisErrorCodes.INSTITUTION_NOT_FOUND,
  TOO_MANY_STUDENT_MATCHES_EXCEPTION: SisErrorCodes.MULTIPLE_STUDENTS_MATCHED,
  TOO_MANY_INSTITUTION_MATCHES_EXCEPTION: SisErrorCodes.MULTIPLE_INSTITUTIONS_MATCHED
};

/** HTTP statuses the API refuses with, mapped onto the same codes. */
const REFUSAL_CODE_BY_STATUS: Record<number, string> = {
  400: SisErrorCodes.CONFIGURATION_ERROR,
  404: SisErrorCodes.CONFIGURATION_ERROR,
  501: SisErrorCodes.CONFIGURATION_ERROR,
  401: SisErrorCodes.AUTHENTICATION_ERROR,
  403: SisErrorCodes.AUTHENTICATION_ERROR
};

/**
 * Exports a transcript through the Document Processor API's tenant connectors.
 *
 * The API resolves the connector from the transcript's type and the tenant in the token,
 * so nothing here knows about configurations or destinations.
 */
@Injectable({
  providedIn: 'root'
})
export class ConnectorExportService {
  private authService = inject(AuthService);
  private autoclassifierSdkService = inject(AutoclassifierSdkService);

  /**
   * Gets authenticated access token, throws if not available
   */
  private async getAccessToken(): Promise<string> {
    const authToken = await this.authService.waitForToken();
    if (!authToken) throw new Error('Not authenticated');
    return authToken;
  }

  /**
   * Reads the fields an export of this transcript type will require.
   * @param transcriptTypeId 0 for College, 1 for High School
   * @returns The requirements, or null when the tenant has no connector for the type
   */
  async getExportRequirements(
    transcriptTypeId: number
  ): Promise<ConnectorExportRequirements | null> {
    return this.autoclassifierSdkService.connectorSdk.getExportRequirements(
      await this.getAccessToken(),
      transcriptTypeId
    );
  }

  /**
   * Sends one transcript to the connector configured for its type.
   * @param transcripts an array of transcript data, id, URL and type
   */
  async exportTranscripts(
    transcripts: TranscriptToExport[]
  ): Promise<BatchSisResponse[] | BatchSisResponse> {
    const payloadItems: any = [];
    try {
      transcripts.forEach(transcript => {
        let data = transcript.data;
        let payload = {
          softdocsTranscriptUrl: transcript.transcriptUrl,
          transcriptType: transcript.transcriptType,
          transcriptId: transcript.transcriptId,
          student: mapSection(data.Student as unknown as Record<string, FieldWithBbox>),
          ...(transcript.transcriptType === TranscriptType.HighSchool
            ? // Only the issuing school, which the extraction puts first.
              { institutions: data.Institutions?.[0] ? [mapSection(data.Institutions[0])] : [] }
            : { courses: mapCourses(data.Courses || []) })
        };
        payloadItems.push(payload);
      });

      const responses = await this.autoclassifierSdkService.connectorSdk.exportTranscripts(
        await this.getAccessToken(),
        payloadItems
      );
      return responses.map(response => {
        return this.mapResponse(response);
      });
    } catch (error) {
      console.error('Connector export failed:', error);
      return {
        success: false,
        code:
          REFUSAL_CODE_BY_STATUS[(error as { status?: number })?.status ?? 0] ??
          SisErrorCodes.UNKNOWN_ERROR,
        message: error instanceof Error ? error.message : 'Request failed'
      };
    }
  }

  /**
   * Collapses the per-institution outcomes into the single response the UI reacts to.
   * @param response The connector response
   * @param transcriptId The transcript the response belongs to
   */
  private mapResponse(response: ConnectorExportResponse): BatchSisResponse {
    if (response.status === 'succeeded') {
      return { success: true, transcriptId: response.transcriptId };
    }

    const failures = response.results.filter(result => !result.success);
    const message = failures
      .map(failure =>
        failure.institutionName
          ? `${failure.institutionName}: ${failure.message ?? ''}`.trim()
          : (failure.message ?? '')
      )
      .join('; ');

    // A partial export carries the code the UI already treats as PartiallyExported.
    const code =
      response.status === 'partial'
        ? SisErrorCodes.PARTIAL_COURSE_UPLOAD_FAILURE
        : (CONNECTOR_ERROR_CODE_MAP[failures[0]?.code ?? ''] ?? SisErrorCodes.UNKNOWN_ERROR);

    return { success: false, code, message, transcriptId: response?.transcriptId };
  }
}
